import React, { useState } from "react";
import {
  Button,
  Modal,
  Checkbox,
  Divider,
  Space,
  Tooltip,
  message,
  Typography,
} from "antd";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  MenuOutlined,
  SettingOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";

// Define a custom column structure for storing in localStorage
export interface CustomColumn {
  key: string;
  title: string;
  visible: boolean;
}

// Sortable Item component for drag-and-drop functionality
const SortableItem = ({ column }: { column: CustomColumn }) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: column.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    padding: "8px 0",
    cursor: "move",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Space>
        <MenuOutlined style={{ color: "#999", marginRight: 8 }} />
        {t(`bookings.${column.key}`, column.title)}
      </Space>
    </div>
  );
};

// Storage key for localStorage
const STORAGE_KEY = "efreightforwarding_booking_columns";

// Function to get default columns configuration
export const getDefaultColumns = (): CustomColumn[] => {
  return [
    { key: "bookingId", title: "Booking ID", visible: true },
    { key: "reference", title: "Reference", visible: true },
    { key: "status", title: "Status", visible: true },
    { key: "transportType", title: "Transport Type", visible: true },
    { key: "bookingType", title: "Booking Type", visible: true },
    { key: "incoterm", title: "Incoterm", visible: true },
    { key: "cargoReadinessDate", title: "Cargo Readiness Date", visible: true },
    { key: "originPort", title: "Origin Port", visible: false },
    { key: "destinationPort", title: "Destination Port", visible: false },
    { key: "supplierName", title: "Supplier", visible: false },
    { key: "createdAt", title: "Created At", visible: false },
    { key: "containerReference", title: "Container Reference", visible: false },
    { key: "containerETA", title: "Container ETA", visible: false },
    { key: "eta", title: "ETA", visible: false },
    { key: "containerETD", title: "Container ETD", visible: false },
    { key: "totalProducts", title: "Total Products", visible: false },
    { key: "totalPieces", title: "Total Pieces", visible: false },
    { key: "totalCartons", title: "Total Cartons", visible: false },
    { key: "totalWeight", title: "Total Weight", visible: false },
    { key: "totalCBM", title: "Total CBM", visible: false },
    { key: "trackAndTrace", title: "Track & Trace", visible: false },
    { key: "deliveryType", title: "Delivery Type", visible: false },
  ];
};

// Function to get user-saved columns or default if none exist
export const getSavedColumns = (): CustomColumn[] => {
  try {
    const savedColumns = localStorage.getItem(STORAGE_KEY);
    if (savedColumns) {
      return JSON.parse(savedColumns);
    }
  } catch (e) {
    console.error("Error loading saved columns:", e);
  }

  return getDefaultColumns();
};

// Apply column configuration to actual table columns
export const applyColumnVisibility = <T extends object>(
  baseColumns: ColumnsType<T>,
  savedColumns: CustomColumn[]
): ColumnsType<T> => {
  // Create a map for quick lookup of column visibility
  const columnMap = savedColumns.reduce(
    (acc, col) => {
      acc[col.key] = { visible: col.visible, index: acc.order.length };
      acc.order.push(col.key);
      return acc;
    },
    { order: [] as string[] } as Record<
      string,
      { visible: boolean; index: number }
    > & { order: string[] }
  );

  // Filter out columns that should be hidden
  const visibleColumns = baseColumns.filter((col) => {
    const key = col.key as string;
    return columnMap[key]?.visible !== false;
  });

  // Sort columns based on the saved order
  visibleColumns.sort((a, b) => {
    const aKey = a.key as string;
    const bKey = b.key as string;
    return (columnMap[aKey]?.index ?? 999) - (columnMap[bKey]?.index ?? 999);
  });

  // Always ensure "actions" column is last if it exists
  const actionsColumnIndex = visibleColumns.findIndex(
    (col) => col.key === "actions"
  );
  if (actionsColumnIndex !== -1) {
    const actionsColumn = visibleColumns.splice(actionsColumnIndex, 1)[0];
    visibleColumns.push(actionsColumn);
  }

  return visibleColumns;
};

interface BookingTableColumnManagerProps {
  onColumnsChange: (columns: CustomColumn[]) => void;
}

export const BookingTableColumnManager: React.FC<
  BookingTableColumnManagerProps
> = ({ onColumnsChange }) => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [columns, setColumns] = useState<CustomColumn[]>(getSavedColumns());

  // Set up drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setColumns((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over?.id);

        const newArray = [...items];
        const [removed] = newArray.splice(oldIndex, 1);
        newArray.splice(newIndex, 0, removed);

        return newArray;
      });
    }
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key: string) => {
    // Don't allow toggling bookingId and status columns
    if (key === "bookingId" || key === "status") return;

    setColumns((prev) =>
      prev.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col
      )
    );
  };

  // Toggle all columns except bookingId and status
  const toggleAllColumns = (checked: boolean) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.key === "bookingId" || col.key === "status"
          ? col
          : { ...col, visible: checked }
      )
    );
  };

  // Calculate if all toggleable columns are selected
  const areAllColumnsSelected = columns.every(
    (col) => col.visible || col.key === "bookingId" || col.key === "status"
  );

  // Save changes to localStorage and notify parent component
  const saveChanges = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
      onColumnsChange(columns);
      setIsModalVisible(false);
      message.success(t("bookings.columnPreferencesSaved"));
    } catch (e) {
      console.error("Error saving column preferences:", e);
      message.error(t("bookings.errorSavingPreferences"));
    }
  };

  // Reset to default column settings
  const resetToDefault = () => {
    const defaultColumns = getDefaultColumns();
    setColumns(defaultColumns);
    // Don't save changes immediately, wait for user to click Save
  };

  return (
    <>
      <Tooltip title={t("bookings.customizeColumns")}>
        <Button
          icon={<SettingOutlined />}
          onClick={() => setIsModalVisible(true)}
          style={{ marginLeft: 8 }}
        />
      </Tooltip>

      <Modal
        title={t("bookings.customizeColumns")}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={saveChanges}
        width={500}
        footer={[
          <Button
            key="reset"
            onClick={resetToDefault}
            icon={<ReloadOutlined />}
          >
            {t("common.resetToDefault")}
          </Button>,
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            {t("common.cancel")}
          </Button>,
          <Button key="save" type="primary" onClick={saveChanges}>
            {t("common.save")}
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography.Text>{t("bookings.selectAll")}</Typography.Text>
            <Checkbox
              checked={areAllColumnsSelected}
              onChange={(e) => toggleAllColumns(e.target.checked)}
            />
          </div>

          <Divider />

          <p>{t("bookings.dragToReorder")}</p>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={columns.map((col) => col.key)}
              strategy={verticalListSortingStrategy}
            >
              {columns.map((column) => (
                <div
                  key={column.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <SortableItem column={column} />
                  <Checkbox
                    checked={column.visible}
                    onChange={() => toggleColumnVisibility(column.key)}
                    disabled={
                      column.key === "bookingId" || column.key === "status"
                    }
                  />
                </div>
              ))}
            </SortableContext>
          </DndContext>
        </Space>
      </Modal>
    </>
  );
};

export default BookingTableColumnManager;
